import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { ArrowRight, Loader2, MapPin, Clock, Route, BadgeCheck, CreditCard, Banknote, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VEHICLES = [
  { id: "eco", model: "Tesla Model Y" },
  { id: "business", model: "Mercedes Classe E" },
  { id: "van", model: "Mercedes Classe V" },
  { id: "prestige", model: "Mercedes Classe S" },
];

export default function BookingWidget({ vehicle, setVehicle }) {
  const navigate = useNavigate();
  const { t } = useLang();
  const [pickup, setPickup] = useState("");
  const [destination, setDestination] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [paying, setPaying] = useState(null);

  const handleQuote = async (e) => {
    e.preventDefault();
    if (!pickup.trim() || !destination.trim()) {
      toast.error(t("w.err1"));
      return;
    }
    setLoadingQuote(true);
    setQuote(null);
    try {
      const res = await axios.post(`${API}/quote`, { pickup, destination, vehicle });
      setQuote(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("w.err2"));
    } finally {
      setLoadingQuote(false);
    }
  };

  const handleBook = async (method) => {
    if (!name.trim() || !email.trim() || !phone.trim()) {
      toast.error(t("w.err3"));
      return;
    }
    setPaying(method);
    try {
      const booking = await axios.post(`${API}/bookings`, {
        pickup, destination, date, time, vehicle, name, email, phone,
        payment_method: method,
      });
      if (method === "stripe") {
        const checkout = await axios.post(`${API}/payments/checkout`, {
          booking_id: booking.data.id,
          origin_url: window.location.origin,
        });
        window.location.href = checkout.data.checkout_url;
      } else {
        navigate(`/confirmation/${booking.data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("w.err4"));
      setPaying(null);
    }
  };

  const inputCls = "gold-input w-full px-4 py-3 text-sm text-white rounded-xl placeholder-[#5a6475]";

  return (
    <div id="simulateur" className="gold-card rounded-2xl p-6 md:p-8 relative overflow-hidden backdrop-blur-md scroll-mt-24" data-testid="booking-widget">
      <div className="flex items-center justify-between pb-5 border-b border-[#222834] mb-6">
        <div className="flex items-center gap-3">
          <span className="w-2.5 h-2.5 rounded-full bg-[#c9a24d] shadow-[0_0_8px_#c9a24d]"></span>
          <span className="font-geom text-sm font-bold tracking-widest uppercase text-white">{t("w.module")}</span>
        </div>
        <span className="font-mono text-[10px] text-[#c9a24d] bg-[#1a1711] border border-[#c9a24d]/40 px-2.5 py-1 rounded-full uppercase tracking-wider">
          {t("w.direct")}
        </span>
      </div>

      <form className="space-y-4" onSubmit={handleQuote}>
        <div>
          <label className="font-mono text-[11px] text-[#a1abb9] uppercase mb-1.5 flex items-center justify-between">
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#c9a24d]"></span> {t("w.pickup")}</span>
            <span className="text-[10px] text-[#c9a24d]">{t("w.dep")}</span>
          </label>
          <input type="text" value={pickup} onChange={(e) => setPickup(e.target.value)} data-testid="pickup-input"
            placeholder={t("w.pickupPh")} className={inputCls} />
        </div>

        <div>
          <label className="font-mono text-[11px] text-[#a1abb9] uppercase mb-1.5 flex items-center justify-between">
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#c9a24d]"></span> {t("w.dest")}</span>
            <span className="text-[10px] text-[#c9a24d]">{t("w.arr")}</span>
          </label>
          <input type="text" value={destination} onChange={(e) => setDestination(e.target.value)} data-testid="destination-input"
            placeholder={t("w.destPh")} className={inputCls} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-mono text-[11px] text-[#a1abb9] uppercase mb-1.5">{t("w.date")}</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="date-input"
              className="gold-input w-full px-3.5 py-2.5 text-sm text-white rounded-xl" />
          </div>
          <div>
            <label className="block font-mono text-[11px] text-[#a1abb9] uppercase mb-1.5">{t("w.time")}</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="time-input"
              className="gold-input w-full px-3.5 py-2.5 text-sm text-white rounded-xl" />
          </div>
        </div>

        <div className="pt-1">
          <label className="block font-mono text-[11px] text-[#a1abb9] uppercase mb-1.5">{t("w.veh")}</label>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {VEHICLES.map((v) => (
              <button type="button" key={v.id} onClick={() => { setVehicle(v.id); setQuote(null); }}
                data-testid={`vehicle-option-${v.id}`}
                className={`rounded-lg p-2.5 text-center transition-colors border ${
                  vehicle === v.id
                    ? "bg-[#12151e] border-[#c9a24d]/60"
                    : "bg-[#0b0e14] border-[#212631] hover:border-[#c9a24d]"
                }`}>
                <span className="font-disp block text-sm text-white font-medium">{t(`veh.${v.id}`)}</span>
                <span className={`font-mono text-[10px] ${vehicle === v.id ? "text-[#c9a24d]" : "text-[#8c98a9]"}`}>{t(`v.${v.id}.cap`)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="pt-3">
          <button type="submit" disabled={loadingQuote} data-testid="quote-submit-button"
            className="w-full py-4 px-6 bg-gradient-to-r from-[#c9a24d] via-[#d9b86c] to-[#a17c2e] hover:brightness-110 text-[#060709] font-bold font-geom text-xs uppercase tracking-[0.2em] rounded-xl transition-all flex items-center justify-center gap-2 shadow-xl shadow-[#c9a24d]/25 border border-[#e3c07a]/40 disabled:opacity-60">
            {loadingQuote ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4 rtl:rotate-180" />}
            {loadingQuote ? t("w.calcLoading") : t("w.calc")}
          </button>
        </div>
      </form>

      {quote && (
        <div className="mt-5 rounded-xl border border-[#c9a24d]/40 bg-[#0b0e14] p-4 space-y-3" data-testid="quote-result">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#8c98a9]">{t("w.fare")}</span>
            <span className="font-disp text-3xl text-[#c9a24d]" data-testid="quote-price">{quote.price_eur.toFixed(2)} €</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs text-[#9ea8b6] font-mono">
            <span className="flex items-center gap-1.5" data-testid="quote-distance"><Route className="w-3.5 h-3.5 text-[#c9a24d]" /> {quote.distance_km} km</span>
            <span className="flex items-center gap-1.5" data-testid="quote-duration"><Clock className="w-3.5 h-3.5 text-[#c9a24d]" /> ~{quote.duration_min} min</span>
            <span className="flex items-center gap-1.5 col-span-2" data-testid="quote-vehicle"><BadgeCheck className="w-3.5 h-3.5 text-[#c9a24d]" /> {quote.vehicle_label}</span>
          </div>

          <div className="pt-3 border-t border-[#212631] space-y-3">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("w.name")} data-testid="client-name-input" className={inputCls} />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("w.email")} data-testid="client-email-input" className={inputCls} />
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("w.phone")} data-testid="client-phone-input" className={inputCls} />
            <button type="button" onClick={() => handleBook("stripe")} disabled={!!paying} data-testid="confirm-pay-button"
              className="w-full py-4 px-6 bg-gradient-to-r from-[#c9a24d] via-[#d9b86c] to-[#a17c2e] hover:brightness-110 text-[#060709] font-bold font-geom text-xs uppercase tracking-[0.2em] rounded-xl transition-all flex items-center justify-center gap-2 shadow-xl shadow-[#c9a24d]/25 border border-[#e3c07a]/40 disabled:opacity-60">
              {paying === "stripe" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {paying === "stripe" ? t("w.redirecting") : `${t("w.payOnline")} ${quote.price_eur.toFixed(2)} €`}
            </button>
            <button type="button" onClick={() => handleBook("especes")} disabled={!!paying} data-testid="pay-cash-button"
              className="w-full py-3.5 px-6 bg-[#12151e] hover:bg-[#1a1f2b] text-white font-bold font-geom text-xs uppercase tracking-[0.2em] rounded-xl transition-all flex items-center justify-center gap-2 border border-[#c9a24d]/40 disabled:opacity-60">
              {paying === "especes" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4 text-[#c9a24d]" />}
              {paying === "especes" ? t("w.confirming") : t("w.cash")}
            </button>
            <button type="button" onClick={() => handleBook("devis")} disabled={!!paying} data-testid="quote-request-button"
              className="w-full py-3 px-6 text-[#c9a24d] hover:text-[#e3c07a] font-bold font-geom text-xs uppercase tracking-[0.2em] rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60">
              {paying === "devis" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              {paying === "devis" ? t("w.sending") : t("w.devis")}
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-[#212631] flex items-center justify-between text-[11px] text-[#8a96a7] font-mono">
        <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {t("w.fr")}</span>
        <span className="text-[#c9a24d] font-medium">{t("w.instant")}</span>
      </div>
    </div>
  );
}
